// ON-464: how a share page presents an event's registration mode (ON-417).
//
// The web stays a hand-off to the app. Nobody registers or applies here; the
// page only has to say *how* spots are given out so the CTA is honest. Open
// events must render exactly as they did before this existed, so every helper
// here treats a missing or unknown mode as 'open'.

import type { RegistrationMode } from '../types/share'

export type ApplyMode = Exclude<RegistrationMode, 'open'>

/** The mode to render, or null for open (first-come) registration. */
export function applyModeOf(mode: string | null | undefined): ApplyMode | null {
  return mode === 'lottery' || mode === 'selection' ? mode : null
}

export interface ApplyState {
  mode: ApplyMode
  /** Total spots the host will fill, or null when uncapped. */
  spots: number | null
  /** Live applications; null when the backend did not send a count. */
  applied: number | null
  /** ISO deadline to show as "Apply by …", or null. */
  applyBy: string | null
  /** The pool is no longer taking applications. */
  closed: boolean
}

interface ApplyFields {
  registration_mode?: string | null
  max_participants: number | null
  interest_count?: number | null
  selection_closed_at?: string | null
  registration_deadline?: string | null
}

/**
 * Everything the event page needs to render a selection / lottery event, or
 * null for an open one (which keeps its "N spots left" layout untouched).
 *
 * Closed when the host has closed the pool, or when the application deadline
 * has passed. A future deadline is shown as "Apply by …"; a past one is not —
 * "Applications closed" says the same thing more usefully.
 */
export function applyStateOf(event: ApplyFields, now: Date = new Date()): ApplyState | null {
  const mode = applyModeOf(event.registration_mode)
  if (!mode) return null

  const deadline = event.registration_deadline ? new Date(event.registration_deadline) : null
  const deadlineValid = deadline != null && !Number.isNaN(deadline.getTime())
  const deadlinePassed = deadlineValid && deadline.getTime() <= now.getTime()
  const closed = Boolean(event.selection_closed_at) || deadlinePassed

  return {
    mode,
    spots: event.max_participants,
    applied: typeof event.interest_count === 'number' ? event.interest_count : null,
    applyBy: !closed && deadlineValid ? event.registration_deadline! : null,
    closed,
  }
}
